import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TtoCampaignVideosDto, TtoCampaignVideoDto } from '../../../../../../common/general-types/tiktok-one-reporting.dto';
import { TtoVideoMetricsComponent } from '../video-metrics/video-metrics';
import { DecimalPipe } from '@angular/common';
import { FormatService } from '@/app/common/format/format.service';
import { TtoVideoAnalysisComponent } from '../video-analysis/video-analysis';

export type VideoColumnKey =
  | 'engagement_count'
  | 'engagement_rate'
  | 'engagement_rate_organic'
  | 'engagement_rate_paid'
  | 'likes'
  | 'likes_organic'
  | 'likes_paid'
  | 'comments'
  | 'comments_organic'
  | 'comments_paid'
  | 'shares'
  | 'shares_organic'
  | 'shares_paid'
  | 'video_views'
  | 'video_views_organic'
  | 'video_views_paid'
  | 'average_view_time'
  | 'average_view_time_organic'
  | 'average_view_time_paid'
  | 'video_completion_rate'
  | 'video_completion_rate_organic'
  | 'video_completion_rate_paid'
  | 'two_seconds_views'
  | 'organic_two_seconds_views'
  | 'paid_two_seconds_views'
  | 'six_seconds_views'
  | 'organic_six_seconds_views'
  | 'paid_six_seconds_views'
  | 'reach'
  | 'reach_organic'
  | 'reach_paid'
  | 'total_play_time'
  | 'ad_cost'
  | 'ad_clicks'
  | 'ad_conversions'
  | 'ad_cpa'
  | 'ad_cpc'
  | 'ad_cpm'
  | 'ad_ctr'
  | 'ad_cvr'
  | 'ad_impressions'
  | 'ad_roas'
  | 'audience_age_distribution'
  | 'audience_countries_distribution'
  | 'audience_device_distribution'
  | 'audience_genders_distribution'
  | 'audience_interest_distribution'
  | 'audience_language_distribution'
  | 'audience_locale_distribution';

export type VideoColumnTab = 'engagement' | 'content-performance' | 'ads-performance' | 'audience';

export interface VideoColumnDef {
  key: VideoColumnKey;
  label: string;
  tab: VideoColumnTab;
}

export const VIDEO_COLUMN_DEFS: VideoColumnDef[] = [
  // Engagement
  { key: 'engagement_count', label: 'Engagement Count', tab: 'engagement' },
  { key: 'engagement_rate', label: 'Engagement Rate', tab: 'engagement' },
  { key: 'engagement_rate_organic', label: 'Engagement Rate (Organic)', tab: 'engagement' },
  { key: 'engagement_rate_paid', label: 'Engagement Rate (Paid)', tab: 'engagement' },
  { key: 'likes', label: 'Likes', tab: 'engagement' },
  { key: 'likes_organic', label: 'Likes (Organic)', tab: 'engagement' },
  { key: 'likes_paid', label: 'Likes (Paid)', tab: 'engagement' },
  { key: 'comments', label: 'Comments', tab: 'engagement' },
  { key: 'comments_organic', label: 'Comments (Organic)', tab: 'engagement' },
  { key: 'comments_paid', label: 'Comments (Paid)', tab: 'engagement' },
  { key: 'shares', label: 'Shares', tab: 'engagement' },
  { key: 'shares_organic', label: 'Shares (Organic)', tab: 'engagement' },
  { key: 'shares_paid', label: 'Shares (Paid)', tab: 'engagement' },
  // Content Performance
  { key: 'video_views', label: 'Total Video Views', tab: 'content-performance' },
  { key: 'video_views_organic', label: 'Video Views (Organic)', tab: 'content-performance' },
  { key: 'video_views_paid', label: 'Video Views (Paid)', tab: 'content-performance' },
  { key: 'average_view_time', label: 'Avg View Time', tab: 'content-performance' },
  { key: 'average_view_time_organic', label: 'Avg View Time (Organic)', tab: 'content-performance' },
  { key: 'average_view_time_paid', label: 'Avg View Time (Paid)', tab: 'content-performance' },
  { key: 'video_completion_rate', label: 'Completion Rate', tab: 'content-performance' },
  { key: 'video_completion_rate_organic', label: 'Completion Rate (Organic)', tab: 'content-performance' },
  { key: 'video_completion_rate_paid', label: 'Completion Rate (Paid)', tab: 'content-performance' },
  { key: 'two_seconds_views', label: '2s Views', tab: 'content-performance' },
  { key: 'organic_two_seconds_views', label: '2s Views (Organic)', tab: 'content-performance' },
  { key: 'paid_two_seconds_views', label: '2s Views (Paid)', tab: 'content-performance' },
  { key: 'six_seconds_views', label: '6s Views', tab: 'content-performance' },
  { key: 'organic_six_seconds_views', label: '6s Views (Organic)', tab: 'content-performance' },
  { key: 'paid_six_seconds_views', label: '6s Views (Paid)', tab: 'content-performance' },
  { key: 'reach', label: 'Reach', tab: 'content-performance' },
  { key: 'reach_organic', label: 'Reach (Organic)', tab: 'content-performance' },
  { key: 'reach_paid', label: 'Reach (Paid)', tab: 'content-performance' },
  { key: 'total_play_time', label: 'Total Play Time', tab: 'content-performance' },
  // Ads Performance
  { key: 'ad_cost', label: 'Ads Cost', tab: 'ads-performance' },
  { key: 'ad_clicks', label: 'Ad Clicks', tab: 'ads-performance' },
  { key: 'ad_conversions', label: 'Ad Conversions', tab: 'ads-performance' },
  { key: 'ad_cpa', label: 'Cost per Acquisition', tab: 'ads-performance' },
  { key: 'ad_cpc', label: 'Cost per Click', tab: 'ads-performance' },
  { key: 'ad_cpm', label: 'Cost per Mille', tab: 'ads-performance' },
  { key: 'ad_ctr', label: 'Click-Through Rate', tab: 'ads-performance' },
  { key: 'ad_cvr', label: 'Conversion Rate', tab: 'ads-performance' },
  { key: 'ad_impressions', label: 'Ad Impressions', tab: 'ads-performance' },
  { key: 'ad_roas', label: 'ROAS', tab: 'ads-performance' },
  // Audience
  { key: 'audience_age_distribution', label: 'Age Distribution', tab: 'audience' },
  { key: 'audience_countries_distribution', label: 'Countries Distribution', tab: 'audience' },
  { key: 'audience_device_distribution', label: 'Device Distribution', tab: 'audience' },
  { key: 'audience_genders_distribution', label: 'Gender Distribution', tab: 'audience' },
  { key: 'audience_interest_distribution', label: 'Interest Distribution', tab: 'audience' },
  { key: 'audience_language_distribution', label: 'Language Distribution', tab: 'audience' },
  { key: 'audience_locale_distribution', label: 'Locale Distribution', tab: 'audience' },
];

export const DEFAULT_VIDEO_COLUMNS = new Set<VideoColumnKey>(['video_views', 'engagement_rate', 'ad_cost']);

@Component({
  selector: 'ttor-videos',
  standalone: true,
  imports: [TtoVideoMetricsComponent, DecimalPipe, TtoVideoAnalysisComponent],
  templateUrl: './videos.html',
  styleUrl: './videos.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TtoVideosComponent {
  videos = input.required<TtoCampaignVideosDto>();
  visibleColumns = signal<Set<VideoColumnKey>>(new Set(DEFAULT_VIDEO_COLUMNS));
  searchQuery = signal('');

  videoForAnalysis = signal<TtoCampaignVideoDto | undefined>(undefined);

  /** Services */
  public formatService = inject(FormatService);
  /** Services END */

  filteredVideos = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return this.videos();
    return this.videos().filter(v =>
      v.creator_info.display_name.toLowerCase().includes(query) ||
      v.creator_info.handle_name.toLowerCase().includes(query)
    );
  });

  isVisible(key: VideoColumnKey): boolean {
    return this.visibleColumns().has(key);
  }

  onColumnsChange(cols: Set<VideoColumnKey>): void {
    this.visibleColumns.set(cols);
  }

  onSearch(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }
}
